import type {
    HelpDocument,
    HelpDocumentId,
    HelpGuide,
    HelpGuideId,
    HelpGuideStep,
    HelpGuideStepAction,
    HelpTreeNode,
} from './types';
/**
 * Loads help documents and guides from Markdown files with YAML frontmatter.
 *
 * Documents live in `./docs/*.md` and guides in `./guides/*.md`.
 * Files are sorted by filename (use number prefixes like `01-name.md` for ordering).
 * Frontmatter is parsed with the in-repo `parseFrontmatter` utility.
 * Guide steps are delimited by `## ` headings with a `targets` fenced code block.
 */
import { parseFrontmatter } from 'src/lib/frontmatter';

// ── Raw markdown file loading via Vite glob ─────────────────────────

const docModules = import.meta.glob('./docs/*.md', { eager: true, query: '?raw', import: 'default' }) as Record<
    string,
    string
>;

const guideModules = import.meta.glob('./guides/*.md', { eager: true, query: '?raw', import: 'default' }) as Record<
    string,
    string
>;

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Derive the content ID from a glob path like `./docs/01-getting-started.md`.
 * Strips the directory, number prefix, and `.md` extension.
 */
function idFromPath(globPath: string): string {
    const filename = globPath.split('/').pop()!;
    return filename.replace(/\.md$/, '').replace(/^\d+-/, '');
}

// ── Validation ──────────────────────────────────────────────────────

function assertNonEmpty(value: unknown, field: string, file: string): asserts value is string {
    if (typeof value !== 'string' || value.trim() === '') {
        throw new Error(`Help content "${file}": "${field}" must be a non-empty string`);
    }
}

function assertPath(value: unknown, file: string): asserts value is string[] {
    if (!Array.isArray(value) || value.length === 0 || value.some((v) => typeof v !== 'string')) {
        throw new Error(`Help content "${file}": "path" must be a non-empty string array`);
    }
}

function assertKeywords(value: unknown, file: string): asserts value is string[] | undefined {
    if (value !== undefined && (!Array.isArray(value) || value.some((v) => typeof v !== 'string'))) {
        throw new Error(`Help content "${file}": "keywords" must be a string array`);
    }
}

// ── Document parsing ────────────────────────────────────────────────

/** Strip markdown formatting to produce a plain-text preview for search results. */
function stripMarkdown(markdown: string): string {
    return markdown
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/\*(.*?)\*/g, '$1')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/^[-*+]\s+/gm, '')
        .replace(/^\d+\.\s+/gm, '')
        .replace(/\n{2,}/g, ' ')
        .trim();
}

function parseDocument(globPath: string, raw: string): HelpDocument {
    const { attributes, body } = parseFrontmatter(raw);
    const { path, title, keywords } = attributes;
    assertPath(path, globPath);
    assertNonEmpty(title, 'title', globPath);
    assertKeywords(keywords, globPath);
    const content = body.trim();
    return {
        id: idFromPath(globPath) as HelpDocumentId,
        path,
        title,
        content,
        preview: stripMarkdown(content),
        keywords,
    };
}

// ── Guide parsing ───────────────────────────────────────────────────

const STEP_HEADING_RE = /^## (.+)$/m;
const TARGETS_BLOCK_RE = /^```targets\n([\s\S]*?)```/m;
const TARGET_LINE_RE = /^- (.+)$/gm;
const ACTION_LINE_RE = /^action:\s*(\w+)$/m;

function parseGuideSteps(body: string): HelpGuideStep[] {
    const steps: HelpGuideStep[] = [];

    // Split on ## headings, keeping the heading text
    const parts = body.split(/^(?=## )/m).filter((p) => p.trim());

    for (const part of parts) {
        const headingMatch = part.match(STEP_HEADING_RE);
        if (!headingMatch) continue;

        const title = headingMatch[1]!.trim();
        const afterHeading = part.slice(headingMatch[0].length).trim();

        // Extract targets block
        const targetsMatch = afterHeading.match(TARGETS_BLOCK_RE);
        if (!targetsMatch) continue;

        const targetsBlock = targetsMatch[1]!;
        const targets: string[] = [];
        let m: RegExpExecArray | null;
        const targetRe = new RegExp(TARGET_LINE_RE.source, 'gm');
        while ((m = targetRe.exec(targetsBlock)) !== null) {
            targets.push(m[1]!.trim());
        }
        if (targets.length === 0) continue;

        // Extract and validate optional action
        const ALLOWED_ACTIONS: Set<string> = new Set<HelpGuideStepAction>(['click']);
        const actionMatch = targetsBlock.match(ACTION_LINE_RE);
        const actionRaw = actionMatch?.[1]?.trim();
        if (actionRaw && !ALLOWED_ACTIONS.has(actionRaw)) {
            throw new Error(`Invalid guide step action "${actionRaw}" in step "${title}"`);
        }
        const action = actionRaw as HelpGuideStepAction | undefined;

        // Content is everything after the targets block
        const content = afterHeading.slice(afterHeading.indexOf(targetsMatch[0]) + targetsMatch[0].length).trim();

        steps.push({
            title,
            content,
            targets: targets as [string, ...string[]],
            ...(action ? { action } : {}),
        });
    }

    return steps;
}

function parseGuide(globPath: string, raw: string): HelpGuide {
    const { attributes, body } = parseFrontmatter(raw);
    const { path, title, description, keywords } = attributes;
    assertPath(path, globPath);
    assertNonEmpty(title, 'title', globPath);
    assertNonEmpty(description, 'description', globPath);
    assertKeywords(keywords, globPath);
    const steps = parseGuideSteps(body);
    if (steps.length === 0) {
        throw new Error(`Help guide "${globPath}": at least one step with targets is required`);
    }

    return {
        id: idFromPath(globPath) as HelpGuideId,
        path,
        title,
        description,
        steps: steps as [HelpGuideStep, ...HelpGuideStep[]],
        keywords,
    };
}

// ── Sort by filename (number prefix) ────────────────────────────────

function sortedEntries(modules: Record<string, string>): [string, string][] {
    return Object.entries(modules).sort(([a], [b]) => a.localeCompare(b));
}

// ── Duplicate ID guard ───────────────────────────────────────────────

function assertUniqueIds<T extends { id: string }>(items: T[], kind: string): void {
    const seen = new Set<string>();
    for (const item of items) {
        if (seen.has(item.id)) {
            throw new Error(`Duplicate help ${kind} id "${item.id}"`);
        }
        seen.add(item.id);
    }
}

// ── Public API ──────────────────────────────────────────────────────

export const helpDocuments: HelpDocument[] = sortedEntries(docModules).map(([path, raw]) => parseDocument(path, raw));
assertUniqueIds(helpDocuments, 'document');

export const helpDocumentById = new Map<HelpDocumentId, HelpDocument>(helpDocuments.map((doc) => [doc.id, doc]));

export const helpGuides: HelpGuide[] = sortedEntries(guideModules).map(([path, raw]) => parseGuide(path, raw));
assertUniqueIds(helpGuides, 'guide');

export const helpGuideById = new Map<HelpGuideId, HelpGuide>(helpGuides.map((g) => [g.id, g]));

export function buildHelpTree(): HelpTreeNode[] {
    const root: HelpTreeNode[] = [];

    for (const doc of helpDocuments) {
        let currentLevel = root;

        for (let i = 0; i < doc.path.length; i++) {
            const segment = doc.path[i]!;
            const isLast = i === doc.path.length - 1;

            let existing = currentLevel.find((node) => node.label === segment);
            if (!existing) {
                existing = {
                    label: segment,
                    documentId: isLast ? doc.id : undefined,
                    children: [],
                };
                currentLevel.push(existing);
            } else if (isLast) {
                existing.documentId = doc.id;
            }

            currentLevel = existing.children;
        }
    }

    return root;
}
